// THE GRAMMAR'S VOCABULARY — the bounds, the sort and filter term shapes, the closed
// collection set a cursor is bound to, and the parsed `ListQuery` / `ListQuerySpec` types
// every other module in this directory is written against.
// Split out of `../list-query.ts`, which is now the barrel over this directory.
// `LIST_COLLECTIONS` is exported only so `./cursor` can check a decoded token against it,
// and stays off the barrel — the set is `ListCollection`, spelled for a membership test.

import type { ListCursorKeyKinds } from './key-kind';

/** Default page size when a caller names none. */
export const LIST_QUERY_DEFAULT_PER_PAGE = 50;
/** Hard ceiling on `per_page` — a caller can't demand an unbounded response. */
export const LIST_QUERY_MAX_PER_PAGE = 100;
/** Cap on the free-text `q` term (a search box, not a document). */
export const LIST_QUERY_MAX_Q = 200;
/** Cap on the opaque cursor token — a keyset payload of a few keys, not a blob. */
export const LIST_QUERY_MAX_CURSOR = 1024;

export type SortDir = 'asc' | 'desc';
/** One resolved sort key — a `field` and a direction. */
export type SortTerm = { field: string; dir: SortDir };

/** Filter comparison operators the grammar admits. */
export const FILTER_OPS = ['eq', 'ne', 'in'] as const;
export type FilterOp = (typeof FILTER_OPS)[number];
/** One resolved filter — `field <op> values`. `eq`/`ne` carry one value; `in` many. */
export type FilterTerm = { field: string; op: FilterOp; values: string[] };

/**
 * One value inside a keyset cursor — the content of a sort/tiebreak column on
 * the last row served.
 *
 * `null` is a position only for a NULLABLE SORT key (#2774), and it has exactly
 * one meaning: the row sits in that key's null block, which every order places
 * LAST — in both directions, so flipping `asc`/`desc` mirrors only the non-null
 * values. That rule is part of what a cursor MEANS (a token inside the null
 * block assumes it), so it is fixed, not a per-key knob. Both seams hold it:
 * `@developerz/db` `buildListClauses` (`NULLS LAST` + a null-aware seek) and
 * `pageInMemory` (`./page`). A tiebreak is never null — the order has to be total.
 */
export type ListCursorValue = string | number | boolean | Date | null;

/**
 * WHICH collection a cursor addresses — stamped into every token and checked on
 * the way back in (#2053).
 *
 * A free string would defeat the point: two collections that spell themselves
 * differently in two places are two collections as far as the check is
 * concerned, and two that spell themselves the same are one. The union is the
 * vocabulary, so a new paged collection has to name itself HERE, once, and the
 * typechecker finds every site that must agree.
 *
 * Order alone could never do this job: `tasks`, `ci_jobs`, `grants` and the
 * admin registries all page by `created_at desc`, so an order check admits any
 * of their cursors into any of the others.
 */
export const ListCollection = {
  AdminAccessRequests: 'admin_access_requests',
  AdminAccounts: 'admin_accounts',
  AdminInvitations: 'admin_invitations',
  AdminOss: 'admin_oss',
  AdminUsers: 'admin_users',
  /** One agent's live tasks (`GET /v1/agents/queue`, #6210), newest-updated first. */
  AgentQueue: 'agent_queue',
  AgentSessions: 'agent_sessions',
  CiJobs: 'ci_jobs',
  Dashboards: 'dashboards',
  Donations: 'donations',
  Grants: 'grants',
  Integrations: 'integrations',
  Issues: 'issues',
  KbPages: 'kb_pages',
  Notifications: 'notifications',
  /** Staff bug reports (`platform_bugs`, plan 101 slice 05) — the `bug list` verb. */
  PlatformBugs: 'platform_bugs',
  PullRequests: 'pull_requests',
  /** One repo's tasks + plans, newest activity first — the MCP `activity list` verb (#5572). */
  RepoActivity: 'repo_activity',
  // No `RepoMemory`, deliberately: the `repo_memory` table's one paged read
  // (`GET /v1/kb/:repoId/pages`) is stamped `kb_pages`, the name its route, view
  // and cursor already use. A second member for it would be a second answer to
  // "which collection is this?", and this union exists to have exactly one.
  /** The enable picker (`GET /v1/repo-enablement`, #7802): every repo, in `full_name` order. */
  RepoEnablement: 'repo_enablement',
  Repos: 'repos',
  Runners: 'runners',
  PromptVars: 'prompt_vars',
  Secrets: 'secrets',
  Tasks: 'tasks',
  Templates: 'agent_templates',
  WebhookDeliveries: 'webhook_deliveries',
  Webhooks: 'webhooks',
} as const;
export type ListCollection = (typeof ListCollection)[keyof typeof ListCollection];

/** Membership test for a decoded token's `c` — an unknown name is not a cursor. */
export const LIST_COLLECTIONS: ReadonlySet<string> = new Set(Object.values(ListCollection));

/** A decoded cursor: the collection and order it was minted under, and the position within it. */
export type ListCursorPosition = {
  /**
   * The collection the token was minted for. A cursor is only meaningful against
   * the read that issued it — its keys are that collection's columns, in that
   * collection's types — so the reader refuses a foreign one instead of seeking
   * with it.
   */
  collection: ListCollection;
  /** The resolved sort at mint time. A later request inherits it. */
  sort: SortTerm[];
  /**
   * The last served row's key values — one per {@link sort} term, plus ONE
   * trailing value for the collection's stable tiebreak (always descending).
   * So `keys.length === sort.length + 1`, always.
   */
  keys: ListCursorValue[];
};

/**
 * A normalized, validated list query — the single value that flows from a route
 * to the DB query builder.
 */
export type ListQuery = {
  /**
   * The collection this query reads — stamped into every cursor it mints and
   * checked against every cursor it accepts (#2053). It rides on the QUERY
   * rather than on each mint call because a `ListQuery` already is "a read of one
   * collection": threading it separately would let the two disagree.
   */
  collection: ListCollection;
  /** Clamped page size. */
  perPage: number;
  /**
   * Where to resume: the previous page's last-row keys, or null on the first
   * page. The store turns it into a row-comparison predicate; it is never a row
   * count. Aligned with {@link sort} by construction — the cursor is what SET
   * `sort` when one was sent.
   */
  after: ListCursorPosition | null;
  /** Resolved sort keys — the cursor's, else the caller's, else the spec's default. */
  sort: SortTerm[];
  /** Resolved filter terms (allow-list already enforced). */
  filters: FilterTerm[];
  /** Free-text search term, or null. */
  q: string | null;
};

/** What a single endpoint permits — the honest capability surface of one collection. */
export type ListQuerySpec = {
  /**
   * Which collection this endpoint pages. REQUIRED: it is what a cursor is
   * stamped with and checked against, and a default would silently make every
   * unnamed collection the same one — exactly the hole #2053 reported.
   */
  collection: ListCollection;
  /** Fields a caller may `sort` by. Omitted/empty → sorting rejected. */
  sortable?: readonly string[];
  /** Fields a caller may `filter` by. Omitted/empty → filtering rejected. */
  filterable?: readonly string[];
  /**
   * Comparison ops the collection actually serves. Omitted ⇒ all of
   * {@link FILTER_OPS}. Narrow it where the backing read cannot express one:
   * `webhook.deliveries` takes ONE webhook id, so an `in`/`ne` term would widen
   * back to the whole account. A refused op has to be stated here rather than in
   * the route, or the OpenAPI document publishes a knob that can only ever 400.
   */
  filterOps?: readonly FilterOp[];
  /** Whether `q` free-text search is served. Default false → `q` rejected. */
  searchable?: boolean;
  /**
   * What each cursor key's COLUMN can hold — the sortable fields and the
   * tiebreak. With it, a cursor whose key the column cannot be compared with is
   * refused HERE as `invalid cursor`, before any read runs (plan 101 row 04-10).
   *
   * The read owns the columns, so the read supplies this: a collection paged
   * through `@developerz/db` `buildListClauses` exports its kinds (the
   * `keyKinds` of its checked column map) and the route passes them through. Do not
   * restate them by hand beside the spec — two spellings of one column's type is
   * the drift this field exists to end.
   *
   * Omitted ⇒ the grammar checks only what the codec can (shape, collection,
   * order, key count). `buildListClauses` still refuses a mismatched key before
   * its statement, as a thrown `ListCursorKeyMismatchError` — so the absence of
   * this field costs the caller a worse error, never a driver fault.
   */
  keyKinds?: ListCursorKeyKinds;
  /** Order applied when the caller names no `sort`. */
  defaultSort?: readonly SortTerm[];
  /** Page size when the caller names no `per_page`. Default {@link LIST_QUERY_DEFAULT_PER_PAGE}. */
  defaultPerPage?: number;
  /** Ceiling on `per_page`. Default {@link LIST_QUERY_MAX_PER_PAGE}. */
  maxPerPage?: number;
};
